import {
  Badge,
  Box,
  Button,
  Center,
  Collapse,
  Container,
  Divider,
  Fieldset,
  Flex,
  Grid,
  Group,
  Image,
  Radio,
  RadioGroup,
  Skeleton,
  Spoiler,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import type { ActionFunctionArgs, LoaderFunctionArgs } from '@remix-run/node'
import {
  Await,
  Form,
  Link,
  redirect,
  useLocation,
  useNavigation,
  useSubmit,
} from '@remix-run/react'
import {
  type MetaFunction,
  data,
  useActionData,
  useLoaderData,
} from '@remix-run/react'
import { useEffect } from 'react'
import { Suspense, useState } from 'react'
import { TbChevronDown, TbChevronUp } from 'react-icons/tb'
import type { LineSessionInfo, UserSessionInfo } from '~/authenticator.server'
import { getUserFormAuthenticator } from '~/authenticator.server'
import {
  createApplicationEntryCommand,
  createEmailVerificationInquiryCommand,
  createSessionEntryCommand,
  createStudentCommand,
  createTemporaryApplicationEntryCommand,
  createTemporarySessionEntryCommand,
  entrySearchEventByIdQuery,
  searchEntriesByUserIdAndEventIdQuery,
  searchUserByEmailQuery,
  signupCommand,
} from '~/bootstrap'
import { Breadcrumbs } from '~/components/breadcrumbs'
import { CustomCheckbox } from '~/components/custom-check-box'
import { CustomHTML } from '~/components/custom-html'
import { CustomRadioGroup } from '~/components/custom-radio-group'
import { CustomTextInput } from '~/components/custom-text-input'
import { CustomTextarea } from '~/components/custom-textarea'
import { FormLabel } from '~/components/form-label'
import { LineButtonWithTooltip } from '~/components/line-button-with-tooltip'
import { SignupForm, signupFormSchema } from '~/components/signup-form'
import { TermPrivacyCheck } from '~/components/term-privacy-check'
import { ERROR_MESSAGES, VALIDATION_MESSAGES } from '~/core/shared/messages'
import { isErrorWithCode } from '~/core/shared/utils/error'
import { generateMetaDescriptor } from '~/core/shared/utils/misc'
import { getSession } from '~/session.server'
import { commitSession } from '~/session.server'

import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { RegisteredUserLoginForm } from '~/components/registered-user-login-form'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advancedFormat)
dayjs.locale('ja')

const SkeletonEventRegistration = () => {
  return (
    <Stack gap='md'>
      <Title order={1}>イベントに申し込む</Title>

      <Stack>
        <Skeleton height={20} width={180} />
        <Skeleton height={240} width={'100%'} />
        <Skeleton height={20} width={'100%'} />
        <Skeleton height={20} width={'100%'} />
        <Skeleton height={20} width={'100%'} />
      </Stack>

      <Fieldset
        legend={<FormLabel title='日程を選択してください' require={false} />}
      >
        {Array.from({ length: 6 }).map((_, index) => {
          const componentKey = `skeleton-session-${index}`
          return (
            <Skeleton height={20} width={'50%'} key={componentKey} mt='sm' />
          )
        })}
      </Fieldset>
    </Stack>
  )
}

const isString = (value: unknown): value is string => {
  return typeof value === 'string'
}

export const meta: MetaFunction = (meta) =>
  generateMetaDescriptor(meta, { pageTitle: '申込み' })

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
  const session = await getSession(request.headers.get('cookie'))
  const user: UserSessionInfo | undefined = session.get('user')
  const line: LineSessionInfo | undefined = session.get('line')
  const isLineConnected = !!line
  const isKnownUser = !!user
  const isEmailVerified = user?.isEmailVerified

  const id = params.id
  if (!id) {
    return redirect('/events')
  }
  const eventPromise = entrySearchEventByIdQuery.execute({
    id,
    limitToOpenEvents: false,
  })
  const entriesPromise = user
    ? searchEntriesByUserIdAndEventIdQuery.execute({
        userId: user.id,
        eventId: id,
      })
    : Promise.resolve([])

  return data({
    isLineConnected,
    isKnownUser,
    isEmailVerified,
    eventPromise,
    entriesPromise,
    eventId: id,
  })
}

export const action = async ({ request, params }: ActionFunctionArgs) => {
  /* やることMEMO
    session確認
      L userSessionInfo
      L lineSessionInfo
      L isEmailVerified
    form確認
      L loginForm (🚀 Authenticator.authenticate)
      L entryForm ユーザーを特定できていて、メール認証済か否かによってEntryを作るかTemporaryEntryを作るか分岐
        L isEmailVerified true メール認証済
          L application系 (🚀 CreateApplicationEntryCommand) → redirect('/events/register/${eventId}/complete')
          L session系 (🚀 CreateSessionEntryCommand) → redirect('/events/register/${eventId}/complete')
        L isEmailVerified false メール認証未済み
          L application系 (🚀 CreateTemporaryApplicationEntryCommand) → redirect('/events/register/${eventId}/sendmail')
          L session系 (🚀 CreateTemporarySessionEntryCommand) → redirect('/events/register/${eventId}/sendmail')
      L entryWithSignupForm 新規ユーザーのため、TemporaryEntryを作成
        L 共通してsignup (🚀 SignupCommand, 🚀 CreateStudentCommand, 🚀 CreateEmailVerificationInquiryCommand)
          L application系 (🚀 CreateTemporaryApplicationEntryCommand)
          L session系 (🚀 CreateTemporarySessionEntryCommand)
  */

  const session = await getSession(request.headers.get('cookie'))
  const sessionUser: UserSessionInfo | undefined = session.get('user')
  const sessionLine: LineSessionInfo | undefined = session.get('line')

  const url = new URL(request.url)
  const submittedForm = url.searchParams.get('form')

  if (submittedForm === 'login') {
    // login form
    try {
      const authenticator = getUserFormAuthenticator()
      const user = await authenticator.authenticate('user', request)
      session.set('user', user)

      // もしユーザーに既にlineIdがあれば、セッションにline情報をセットする
      if (user.lineId) {
        session.set('line', { lineId: user.lineId })
      }
    } catch (error) {
      const errorCode = isErrorWithCode(error) ? error.errorCode : 5000
      const severity =
        error && typeof error === 'object' && 'severity' in error
          ? error.severity
          : 'error'
      return {
        loginFormApplicationError: ERROR_MESSAGES[errorCode],
        loginFormSeverity: severity,
      }
    }

    return redirect(request.url, {
      headers: { 'Set-Cookie': await commitSession(session) },
    })
  }

  if (submittedForm === 'entryWithSignup') {
    // entryWithSignup form
    const eventId = params.id
    if (!eventId) {
      return redirect('/events')
    }
    const rawFormData = await request.formData()
    const formData = Object.fromEntries(rawFormData)

    const eventSessionId = formData.eventSessionId
    const eventApplicationId = formData.eventApplicationId
    // どっちかがないと困る
    if (!eventSessionId && !eventApplicationId) {
      return { signupFormApplicationError: 'イベントが選択されていません' }
    }

    // 両方あっても困る
    if (eventSessionId && eventApplicationId) {
      return { signupFormApplicationError: '複数のイベントが選択されています' }
    }

    const signupFormValidationResult = signupFormSchema.safeParse({
      graduate: null,
      graduateFaculty: null,
      graduateDepartment: null,
      ...formData,
    })

    if (!signupFormValidationResult.success)
      return {
        signupFormValidationErrors: signupFormValidationResult.error.flatten(),
        signupFormApplicationError: VALIDATION_MESSAGES.INVALID_MESSAGE,
      }

    const validFormData = {
      ...signupFormValidationResult.data,
      graduate: signupFormValidationResult.data.graduate ?? null,
      graduateFaculty: signupFormValidationResult.data.graduateFaculty ?? null,
      graduateDepartment:
        signupFormValidationResult.data.graduateDepartment ?? null,
    }

    // 追加　カスタムフォーム対応
    // TODO: 余裕あればValueのみならず、Titleもほしい
    const customTextareaField1Value = formData['custom-textarea-1']
      ? formData['custom-textarea-1'].toString()
      : null
    const customTextareaField2Value = formData['custom-textarea-2']
      ? formData['custom-textarea-2'].toString()
      : null
    const customTextareaField3Value = formData['custom-textarea-3']
      ? formData['custom-textarea-3'].toString()
      : null
    const customTextareaField4Value = formData['custom-textarea-4']
      ? formData['custom-textarea-4'].toString()
      : null
    const customTextareaField5Value = formData['custom-textarea-5']
      ? formData['custom-textarea-5'].toString()
      : null
    const customTextInputField1Value = formData['custom-text-input-1']
      ? formData['custom-text-input-1'].toString()
      : null
    const customTextInputField2Value = formData['custom-text-input-2']
      ? formData['custom-text-input-2'].toString()
      : null
    const customTextInputField3Value = formData['custom-text-input-3']
      ? formData['custom-text-input-3'].toString()
      : null
    const customTextInputField4Value = formData['custom-text-input-4']
      ? formData['custom-text-input-4'].toString()
      : null
    const customTextInputField5Value = formData['custom-text-input-5']
      ? formData['custom-text-input-5'].toString()
      : null
    const customRadioButtonField1Value = formData['custom-radio-1']
      ? formData['custom-radio-1'].toString()
      : null
    const customRadioButtonField2Value = formData['custom-radio-2']
      ? formData['custom-radio-2'].toString()
      : null
    const customRadioButtonField3Value = formData['custom-radio-3']
      ? formData['custom-radio-3'].toString()
      : null
    const customRadioButtonField4Value = formData['custom-radio-4']
      ? formData['custom-radio-4'].toString()
      : null
    const customRadioButtonField5Value = formData['custom-radio-5']
      ? formData['custom-radio-5'].toString()
      : null
    const customCheckboxField1Value = formData['custom-checkbox-1']
      ? formData['custom-checkbox-1'].toString()
      : null
    const customCheckboxField2Value = formData['custom-checkbox-2']
      ? formData['custom-checkbox-2'].toString()
      : null
    const customCheckboxField3Value = formData['custom-checkbox-3']
      ? formData['custom-checkbox-3'].toString()
      : null
    const customCheckboxField4Value = formData['custom-checkbox-4']
      ? formData['custom-checkbox-4'].toString()
      : null
    const customCheckboxField5Value = formData['custom-checkbox-5']
      ? formData['custom-checkbox-5'].toString()
      : null

    try {
      await signupCommand.execute({
        email: validFormData.email,
        password: validFormData.password,
        passwordConfirmation: validFormData.passwordConfirmation,
        lineId: sessionUser?.lineId ?? sessionLine?.lineId ?? null, // ベストエフォート
      })

      // find id and email of created user
      const newUser = await searchUserByEmailQuery.execute({
        email: validFormData.email,
      })

      await createStudentCommand.execute({
        ...validFormData,
        userId: newUser.id,
      })

      await createEmailVerificationInquiryCommand.execute({
        userId: newUser.id,
        email: newUser.email,
      })

      // TemporaryEntryを作成
      if (eventSessionId) {
        if (!isString(eventSessionId)) {
          throw new Error('イベント情報の形式が異なります')
        }
        await createTemporarySessionEntryCommand.execute({
          eventId,
          sessionId: eventSessionId,
          userId: newUser.id,
          customTextareaField1Value,
          customTextareaField2Value,
          customTextareaField3Value,
          customTextareaField4Value,
          customTextareaField5Value,
          customTextInputField1Value,
          customTextInputField2Value,
          customTextInputField3Value,
          customTextInputField4Value,
          customTextInputField5Value,
          customRadioButtonField1Value,
          customRadioButtonField2Value,
          customRadioButtonField3Value,
          customRadioButtonField4Value,
          customRadioButtonField5Value,
          customCheckboxField1Value,
          customCheckboxField2Value,
          customCheckboxField3Value,
          customCheckboxField4Value,
          customCheckboxField5Value,
        })
      }
      if (eventApplicationId) {
        if (!isString(eventApplicationId)) {
          throw new Error('イベント情報の形式が異なります')
        }
        await createTemporaryApplicationEntryCommand.execute({
          eventId,
          applicationId: eventApplicationId,
          userId: newUser.id,
          customTextareaField1Value,
          customTextareaField2Value,
          customTextareaField3Value,
          customTextareaField4Value,
          customTextareaField5Value,
          customTextInputField1Value,
          customTextInputField2Value,
          customTextInputField3Value,
          customTextInputField4Value,
          customTextInputField5Value,
          customRadioButtonField1Value,
          customRadioButtonField2Value,
          customRadioButtonField3Value,
          customRadioButtonField4Value,
          customRadioButtonField5Value,
          customCheckboxField1Value,
          customCheckboxField2Value,
          customCheckboxField3Value,
          customCheckboxField4Value,
          customCheckboxField5Value,
        })
      }

      return redirect(`/events/register/${eventId}/sendmail`)
    } catch (error) {
      const errorCode = isErrorWithCode(error) ? error.errorCode : 5000
      return { signupFormApplicationError: ERROR_MESSAGES[errorCode] }
    }
  }

  if (submittedForm === 'entry') {
    const rawFormData = await request.formData()
    const formData = Object.fromEntries(rawFormData)

    // entry form
    const eventId = params.id
    if (!eventId) {
      return redirect('/events')
    }

    // ユーザーを特定できている前提
    if (!sessionUser) {
      return redirect(`/events/register/${eventId}`)
    }

    const eventSessionId = formData.eventSessionId
    const eventApplicationId = formData.eventApplicationId
    // どっちかがないと困る
    if (!eventSessionId && !eventApplicationId) {
      return { entryError: 'イベントが選択されていません' }
    }

    // 両方あっても困る
    if (eventSessionId && eventApplicationId) {
      return { entryError: '複数のイベントが選択されています' }
    }

    try {
      // ４パターン（user.isEmailVerifiedがtrue/false、eventSessionId/eventApplicationIdがあるかないか）
      // 1. isEmailVerified true
      //    - eventSessionIdがある → CreateSessionEntryCommand
      //    - eventApplicationIdがある → CreateApplicationEntryCommand
      // 2. isEmailVerified false
      //    - eventSessionIdがある → CreateTemporarySessionEntryCommand
      //    - eventApplicationIdがある → CreateTemporaryApplicationEntryCommand

      // 追加　カスタムフォーム対応
      const customTextareaField1Value = formData['custom-textarea-1']
        ? formData['custom-textarea-1'].toString()
        : null
      const customTextareaField2Value = formData['custom-textarea-2']
        ? formData['custom-textarea-2'].toString()
        : null
      const customTextareaField3Value = formData['custom-textarea-3']
        ? formData['custom-textarea-3'].toString()
        : null
      const customTextareaField4Value = formData['custom-textarea-4']
        ? formData['custom-textarea-4'].toString()
        : null
      const customTextareaField5Value = formData['custom-textarea-5']
        ? formData['custom-textarea-5'].toString()
        : null
      const customTextInputField1Value = formData['custom-text-input-1']
        ? formData['custom-text-input-1'].toString()
        : null
      const customTextInputField2Value = formData['custom-text-input-2']
        ? formData['custom-text-input-2'].toString()
        : null
      const customTextInputField3Value = formData['custom-text-input-3']
        ? formData['custom-text-input-3'].toString()
        : null
      const customTextInputField4Value = formData['custom-text-input-4']
        ? formData['custom-text-input-4'].toString()
        : null
      const customTextInputField5Value = formData['custom-text-input-5']
        ? formData['custom-text-input-5'].toString()
        : null
      const customRadioButtonField1Value = formData['custom-radio-1']
        ? formData['custom-radio-1'].toString()
        : null
      const customRadioButtonField2Value = formData['custom-radio-2']
        ? formData['custom-radio-2'].toString()
        : null
      const customRadioButtonField3Value = formData['custom-radio-3']
        ? formData['custom-radio-3'].toString()
        : null
      const customRadioButtonField4Value = formData['custom-radio-4']
        ? formData['custom-radio-4'].toString()
        : null
      const customRadioButtonField5Value = formData['custom-radio-5']
        ? formData['custom-radio-5'].toString()
        : null
      const customCheckboxField1Value = formData['custom-checkbox-1']
        ? formData['custom-checkbox-1'].toString()
        : null
      const customCheckboxField2Value = formData['custom-checkbox-2']
        ? formData['custom-checkbox-2'].toString()
        : null
      const customCheckboxField3Value = formData['custom-checkbox-3']
        ? formData['custom-checkbox-3'].toString()
        : null
      const customCheckboxField4Value = formData['custom-checkbox-4']
        ? formData['custom-checkbox-4'].toString()
        : null
      const customCheckboxField5Value = formData['custom-checkbox-5']
        ? formData['custom-checkbox-5'].toString()
        : null

      if (eventSessionId && !isString(eventSessionId)) {
        throw new Error('イベント情報の形式が異なります')
      }

      if (eventApplicationId && !isString(eventApplicationId)) {
        throw new Error('イベント情報の形式が異なります')
      }

      if (!sessionUser.isEmailVerified && eventSessionId) {
        await createTemporarySessionEntryCommand.execute({
          eventId,
          sessionId: eventSessionId,
          userId: sessionUser.id,
          customTextareaField1Value,
          customTextareaField2Value,
          customTextareaField3Value,
          customTextareaField4Value,
          customTextareaField5Value,
          customTextInputField1Value,
          customTextInputField2Value,
          customTextInputField3Value,
          customTextInputField4Value,
          customTextInputField5Value,
          customRadioButtonField1Value,
          customRadioButtonField2Value,
          customRadioButtonField3Value,
          customRadioButtonField4Value,
          customRadioButtonField5Value,
          customCheckboxField1Value,
          customCheckboxField2Value,
          customCheckboxField3Value,
          customCheckboxField4Value,
          customCheckboxField5Value,
        })
        return redirect(`/events/register/${eventId}/sendmail`)
      }

      if (!sessionUser.isEmailVerified && eventApplicationId) {
        await createTemporaryApplicationEntryCommand.execute({
          eventId,
          applicationId: eventApplicationId,
          userId: sessionUser.id,
          customTextareaField1Value,
          customTextareaField2Value,
          customTextareaField3Value,
          customTextareaField4Value,
          customTextareaField5Value,
          customTextInputField1Value,
          customTextInputField2Value,
          customTextInputField3Value,
          customTextInputField4Value,
          customTextInputField5Value,
          customRadioButtonField1Value,
          customRadioButtonField2Value,
          customRadioButtonField3Value,
          customRadioButtonField4Value,
          customRadioButtonField5Value,
          customCheckboxField1Value,
          customCheckboxField2Value,
          customCheckboxField3Value,
          customCheckboxField4Value,
          customCheckboxField5Value,
        })
        return redirect(`/events/register/${eventId}/sendmail`)
      }

      if (sessionUser.isEmailVerified && eventSessionId) {
        await createSessionEntryCommand.execute({
          eventId,
          limitToOpenEvents: false,
          selectedEventSessionId: eventSessionId,
          userId: sessionUser.id,
          customTextareaField1Value,
          customTextareaField2Value,
          customTextareaField3Value,
          customTextareaField4Value,
          customTextareaField5Value,
          customTextInputField1Value,
          customTextInputField2Value,
          customTextInputField3Value,
          customTextInputField4Value,
          customTextInputField5Value,
          customRadioButtonField1Value,
          customRadioButtonField2Value,
          customRadioButtonField3Value,
          customRadioButtonField4Value,
          customRadioButtonField5Value,
          customCheckboxField1Value,
          customCheckboxField2Value,
          customCheckboxField3Value,
          customCheckboxField4Value,
          customCheckboxField5Value,
        })
        return redirect(`/events/register/${eventId}/complete`)
      }

      if (sessionUser.isEmailVerified && eventApplicationId) {
        await createApplicationEntryCommand.execute({
          eventId,
          limitToOpenEvents: false,
          selectedApplicationId: eventApplicationId,
          userId: sessionUser.id,
          customTextareaField1Value,
          customTextareaField2Value,
          customTextareaField3Value,
          customTextareaField4Value,
          customTextareaField5Value,
          customTextInputField1Value,
          customTextInputField2Value,
          customTextInputField3Value,
          customTextInputField4Value,
          customTextInputField5Value,
          customRadioButtonField1Value,
          customRadioButtonField2Value,
          customRadioButtonField3Value,
          customRadioButtonField4Value,
          customRadioButtonField5Value,
          customCheckboxField1Value,
          customCheckboxField2Value,
          customCheckboxField3Value,
          customCheckboxField4Value,
          customCheckboxField5Value,
        })
        return redirect(`/events/register/${eventId}/complete`)
      }
    } catch (error) {
      const errorCode = isErrorWithCode(error) ? error.errorCode : 5000
      return { entryError: ERROR_MESSAGES[errorCode] }
    }
  }
  return null
}

export default function EventRegistration() {
  const {
    isLineConnected,
    isKnownUser,
    eventPromise,
    entriesPromise,
    eventId,
  } = useLoaderData<typeof loader>()

  const actionData = useActionData<typeof action>()
  const navigation = useNavigation()
  const submit = useSubmit()
  const [isLoading, setIsLoading] = useState(false)
  const currentLocation = useLocation()

  // selectedApplicationIdとselectedSessionIdは他のフォームとの関係が複雑なため、controlledにする。いろんなフォームがSubmitされる際にJSにより値をFormに追加する
  const [selectedApplicationId, setSelectedApplicationId] = useState<string>()
  const [selectedSessionId, setSelectedSessionId] = useState<string>()

  const [customFormErrors, setCustomFormErrors] = useState<
    { name: string; errorMessage: string }[]
  >([])
  const [applicationError, setApplicationError] = useState<string | null>(null)

  // 真の副作用
  useEffect(() => {
    eventPromise.then((events) => {
      const event = events.at(0)
      if (!event) return

      setSelectedSessionId(event?.upcomingEventSessionId ?? undefined)
      setSelectedApplicationId(event?.application?.id)
    })
  }, [eventPromise])

  const handleEntryWithSignupFormSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    setIsLoading(true)
    const form = event.currentTarget as HTMLFormElement
    const formData = new FormData(form)

    if (selectedSessionId) formData.append('eventSessionId', selectedSessionId)
    if (selectedApplicationId)
      formData.append('eventApplicationId', selectedApplicationId)

    // カスタムフォーム
    const customForm = document.querySelector(
      'form#customForm',
    ) as HTMLFormElement | null

    if (customForm) {
      const customFormData = new FormData(customForm)
      const customFormValues = Object.fromEntries(customFormData.entries())

      // カスタムフォームバリデーション
      const requiredFields = customForm.querySelectorAll('[required]')
      const customFormErrors: { name: string; errorMessage: string }[] = []
      for (const field of requiredFields) {
        const input = field as HTMLInputElement | HTMLTextAreaElement
        if (!input.value.trim()) {
          customFormErrors.push({
            name: input.name,
            errorMessage: '必須項目です',
          })
        }
      }

      setCustomFormErrors(customFormErrors) // customFormはエラーだけcontrolled
      setApplicationError(null)

      if (customFormErrors.length > 0) {
        setApplicationError(VALIDATION_MESSAGES.INVALID_MESSAGE)
        setIsLoading(false)
        return
      }

      // merge customForm values into entry form data
      for (const [key, value] of Object.entries(customFormValues)) {
        formData.append(key, value as string)
      }
    }

    submit(formData, {
      method: 'post',
      action: `/events/register/${eventId}?form=entryWithSignup`,
    })
    setIsLoading(false)
  }

  const handleEntryFormSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    setIsLoading(true)
    const form = event.currentTarget as HTMLFormElement
    const formData = new FormData(form)
    if (selectedSessionId) formData.append('eventSessionId', selectedSessionId)
    if (selectedApplicationId)
      formData.append('eventApplicationId', selectedApplicationId)

    // カスタムフォーム
    const customForm = document.querySelector(
      'form#customForm',
    ) as HTMLFormElement | null
    if (customForm) {
      const customFormData = new FormData(customForm)
      const customFormValues = Object.fromEntries(customFormData.entries())

      // カスタムフォームバリデーション
      const requiredFields = customForm.querySelectorAll('[required]')
      const customFormErrors: { name: string; errorMessage: string }[] = []
      for (const field of requiredFields) {
        const input = field as HTMLInputElement | HTMLTextAreaElement
        if (!input.value.trim()) {
          customFormErrors.push({
            name: input.name,
            errorMessage: '必須項目です',
          })
        }
      }

      setCustomFormErrors(customFormErrors) // customFormはエラーだけcontrolled
      setApplicationError(null)

      if (customFormErrors.length > 0) {
        setApplicationError(VALIDATION_MESSAGES.INVALID_MESSAGE)
        setIsLoading(false)
        return
      }

      // merge customForm values into entry form data
      for (const [key, value] of Object.entries(customFormValues)) {
        formData.append(key, value as string)
      }
    }

    submit(formData, {
      method: 'post',
      action: `/events/register/${eventId}?form=entry`,
    })
    setIsLoading(false)
  }

  const formatCapacity = (
    isManualAdmission: boolean,
    attendees: number,
    capacity: number,
  ) => {
    if (isManualAdmission) {
      return (
        <Badge color='error' variant='filled' radius='md'>
          抽選
        </Badge>
      )
    }
    if (attendees === capacity) {
      return (
        <Badge color='error' variant='filled' radius='md'>
          満席
        </Badge>
      )
    }
    if (attendees / capacity > 0.6) {
      return (
        <Badge color='primary' variant='filled' radius='md'>
          残りわずか
        </Badge>
      )
    }
    return (
      <Badge color='success' variant='filled' radius='md'>
        空きあり
      </Badge>
    )
  }

  const entryStatusBadge = (
    isManualAdmission: boolean,
    status: string | undefined,
  ) => {
    if (!isManualAdmission && status === 'Complete') {
      return (
        <Badge color='primary' variant='light' radius='md'>
          応募済み
        </Badge>
      )
    }
    if (isManualAdmission && status === 'AdmissionInProgress') {
      return (
        <Badge color='primary' variant='light' radius='md'>
          結果待ち
        </Badge>
      )
    }
    if (isManualAdmission && status === 'Complete') {
      return (
        <Badge color='primary' variant='light' radius='md'>
          当選
        </Badge>
      )
    }
    if (isManualAdmission && status === 'Rejected') {
      return (
        <Badge color='primary' variant='light' radius='md'>
          落選
        </Badge>
      )
    }
  }

  return (
    <>
      {/* PC */}
      <Box py='sm' pl='xl' visibleFrom='sm'>
        <Suspense fallback={null}>
          <Await resolve={eventPromise} errorElement={<div>エラー</div>}>
            {(events) => {
              const event = events.at(0)
              if (!event) {
                return null
              }
              return (
                <Breadcrumbs
                  items={[
                    {
                      id: 'events',
                      title: 'イベント一覧',
                      href: '/events',
                    },
                    {
                      id: `event-${event.id}`,
                      title: event.title,
                      href: `/events/${event.id}`,
                    },
                    {
                      id: `event-register-${event.id}`,
                      title: 'イベント登録',
                    },
                  ]}
                />
              )
            }}
          </Await>
        </Suspense>
      </Box>

      {/* SP */}
      <Box py='sm' pl='lg' hiddenFrom='sm'>
        <Suspense fallback={null}>
          <Await resolve={eventPromise} errorElement={<div>エラー</div>}>
            {(events) => {
              const event = events.at(0)
              if (!event) {
                return null
              }
              return (
                <Breadcrumbs
                  items={[
                    {
                      id: 'events',
                      title: 'イベント一覧',
                      href: '/events',
                    },
                    {
                      id: `event-${event.id}`,
                      title: event.title,
                      href: `/events/${event.id}`,
                    },
                    {
                      id: `event-register-${event.id}`,
                      title: 'イベント登録',
                    },
                  ]}
                />
              )
            }}
          </Await>
        </Suspense>
      </Box>

      <Suspense fallback={<SkeletonEventRegistration />}>
        <Await resolve={eventPromise} errorElement={<div>エラー</div>}>
          {(events) => {
            const event = events.at(0)

            if (!event) {
              return <Text>イベントが見つかりませんでした</Text>
            }

            return (
              <Suspense fallback={<SkeletonEventRegistration />}>
                <Await
                  resolve={entriesPromise}
                  errorElement={<div>エラー</div>}
                >
                  {(entries) => {
                    const validEntries = entries.filter(
                      (entry) => entry.status !== 'Cancelled',
                    )

                    const isAlreadyApplied = event.isApplication
                      ? validEntries.some(
                          (entry) =>
                            entry.applicationId === selectedApplicationId,
                        )
                      : validEntries.some(
                          (entry) => entry.sessionId === selectedSessionId,
                        )

                    const validApplicationEntry = validEntries.find(
                      (entry) => entry.applicationId === event.application?.id,
                    )

                    return (
                      <Container mb='xl' py='xl' size='lg'>
                        <Stack gap='md'>
                          <Title order={1}>イベントに申し込む</Title>
                          <Link to={`/events/${event.id}`}>
                            <Title order={2}>{event.title}</Title>
                          </Link>
                          <Image
                            src={event.coverUrl}
                            alt={`${event.title}のヘッダー画像`}
                            fit='contain'
                          />
                          <Spoiler
                            maxHeight={220}
                            mb='md'
                            showLabel={
                              <Center mt={'md'}>
                                <Text fw='bold' size='md'>
                                  詳細を見る
                                </Text>
                                <TbChevronDown size={16} />
                              </Center>
                            }
                            hideLabel={
                              <Center mt={'md'}>
                                <Text fw='bold' size='md'>
                                  閉じる
                                </Text>
                                <TbChevronUp size={16} />
                              </Center>
                            }
                            transitionDuration={0}
                            styles={{
                              control: { width: '100%' },
                            }}
                          >
                            <CustomHTML content={event.description ?? ''} />
                          </Spoiler>
                          {event.application ? (
                            <Center>
                              <Flex
                                direction={{ base: 'column', sm: 'row' }}
                                align={{ base: 'center', sm: 'center' }}
                                justify={{ base: 'center', sm: 'center' }}
                              >
                                <Text
                                  size='lg'
                                  my={{ base: 'xs', sm: 'md' }}
                                  fw='bold'
                                >
                                  応募受付期間：
                                  {dayjs(event.applicationStartAt)
                                    .tz('Asia/Tokyo')
                                    .format('M/D H:mm')}
                                  {' 〜 '}
                                  {event.applicationEndAt
                                    ? dayjs(event.applicationEndAt)
                                        .tz('Asia/Tokyo')
                                        .format('M/D H:mm')
                                    : ''}
                                </Text>
                                <Text my={{ base: 'xs', sm: 'md' }} ml='md'>
                                  {entryStatusBadge(
                                    event.application.isManualAdmission,
                                    validApplicationEntry?.status,
                                  )}
                                </Text>
                              </Flex>
                            </Center>
                          ) : null}
                          {!isKnownUser ? (
                            <RegisteredUserLoginForm
                              eventId={eventId}
                              actionData={
                                actionData?.loginFormApplicationError
                                  ? {
                                      applicationError:
                                        actionData.loginFormApplicationError,
                                    }
                                  : undefined
                              }
                              redirectToPathOnSuccessfulLineLogin={`/auth/line?redirectTo=${encodeURIComponent(
                                currentLocation.pathname +
                                  currentLocation.search,
                              )}`}
                            />
                          ) : null}
                          {/* セッション系イベント */}
                          {!event.isApplication ? (
                            <>
                              <Fieldset
                                legend={
                                  <FormLabel
                                    title='日程を選択してください'
                                    require={false}
                                  />
                                }
                                mt='md'
                              >
                                <RadioGroup
                                  value={selectedSessionId ?? ''}
                                  onChange={(value) => {
                                    setSelectedSessionId(value)
                                  }}
                                  name='eventSessionId'
                                  required
                                >
                                  {event.eventSessions.map((es) => {
                                    const eventStartAt = dayjs(es.startAt)
                                      .tz('Asia/Tokyo')
                                      .format('M/D HH:mm')
                                    const eventEndAt = dayjs(es.endAt)
                                      .tz('Asia/Tokyo')
                                      .format('HH:mm')

                                    const eventSessionId = es.id
                                    const validEntry = validEntries.find(
                                      (entry) =>
                                        entry.sessionId === eventSessionId,
                                    )

                                    const applicationEndAt = dayjs(
                                      es.applicationEndAt,
                                    ).format('M/D HH:mm')

                                    return (
                                      <Radio
                                        my='lg'
                                        key={es.id}
                                        name='eventSessionId'
                                        disabled={
                                          dayjs(es.startAt).isBefore(dayjs()) ||
                                          dayjs(es.applicationEndAt).isBefore(
                                            dayjs(),
                                          ) ||
                                          !!validEntry
                                        } // MEMO: 過去分はDisabledにする
                                        onChange={(event) => {
                                          setSelectedSessionId(
                                            event.currentTarget.value,
                                          )
                                        }}
                                        label={
                                          <Group justify='start' align='center'>
                                            <Text fw='bold'>{`${eventStartAt} 〜 ${eventEndAt}`}</Text>
                                            <Text size='md' c='secondary'>
                                              〆切: {applicationEndAt}
                                            </Text>
                                            <Text
                                              size='md'
                                              fw='bold'
                                              c='secondary'
                                            >
                                              {es.place === 'オンライン'
                                                ? es.place
                                                : es.address}
                                            </Text>

                                            {formatCapacity(
                                              es.isManualAdmission,
                                              es.attendees,
                                              es.capacity,
                                            )}

                                            {entryStatusBadge(
                                              es.isManualAdmission,
                                              validEntry?.status,
                                            )}
                                          </Group>
                                        }
                                        value={es.id}
                                        checked={selectedSessionId === es.id}
                                      />
                                    )
                                  })}
                                </RadioGroup>
                              </Fieldset>

                              {selectedSessionId
                                ? (() => {
                                    const selectedSession =
                                      event.eventSessions.find(
                                        (es) => es.id === selectedSessionId,
                                      )
                                    if (!selectedSession) return null
                                    if (!selectedSession.customFields.length)
                                      return null

                                    let textInputCount = 0
                                    let textareaCount = 0
                                    let radioButtonCount = 0
                                    let checkBoxCount = 0

                                    return (
                                      <Stack>
                                        <Form id='customForm'>
                                          <Fieldset
                                            legend={
                                              <FormLabel
                                                title='ご記入ください'
                                                require={false}
                                              />
                                            }
                                          >
                                            <Stack>
                                              {selectedSession.customFields.map(
                                                (field, idx) => {
                                                  switch (field.fieldType) {
                                                    case 'textInput':
                                                      textInputCount++
                                                      return (
                                                        <CustomTextInput
                                                          key={`custom-text-input-${idx}-${textInputCount}`}
                                                          name={`custom-text-input-${textInputCount}`}
                                                          title={field.title}
                                                          description={
                                                            field.description
                                                          }
                                                          required={
                                                            field.required
                                                          }
                                                          error={
                                                            customFormErrors.find(
                                                              (error) =>
                                                                error.name ===
                                                                `custom-text-input-${textInputCount}`,
                                                            )?.errorMessage
                                                          }
                                                        />
                                                      )
                                                    case 'textarea': {
                                                      textareaCount++
                                                      return (
                                                        <CustomTextarea
                                                          key={`custom-textarea-${idx}-${textareaCount}`}
                                                          name={`custom-textarea-${textareaCount}`}
                                                          title={field.title}
                                                          description={
                                                            field.description
                                                          }
                                                          required={
                                                            field.required
                                                          }
                                                          error={
                                                            customFormErrors.find(
                                                              (error) =>
                                                                error.name ===
                                                                `custom-textarea-${textareaCount}`,
                                                            )?.errorMessage
                                                          }
                                                        />
                                                      )
                                                    }
                                                    case 'radioButton':
                                                      radioButtonCount++
                                                      return (
                                                        <CustomRadioGroup
                                                          key={`custom-radio-${idx}-${radioButtonCount}`}
                                                          name={`custom-radio-${radioButtonCount}`}
                                                          title={field.title}
                                                          description={
                                                            field.description ??
                                                            ''
                                                          }
                                                          options={
                                                            field.options
                                                          }
                                                          error={
                                                            customFormErrors.find(
                                                              (error) =>
                                                                error.name ===
                                                                `custom-radio-${radioButtonCount}`,
                                                            )?.errorMessage
                                                          }
                                                        />
                                                      )
                                                    case 'checkBox':
                                                      checkBoxCount++
                                                      return (
                                                        <CustomCheckbox
                                                          key={`custom-checkbox-${idx}-${checkBoxCount}`}
                                                          name={`custom-checkbox-${checkBoxCount}`}
                                                          title={field.title}
                                                          description={
                                                            field.description ??
                                                            ''
                                                          }
                                                          options={
                                                            field.options
                                                          }
                                                          error={
                                                            customFormErrors.find(
                                                              (error) =>
                                                                error.name ===
                                                                `custom-checkbox-${checkBoxCount}`,
                                                            )?.errorMessage
                                                          }
                                                        />
                                                      )
                                                  }
                                                },
                                              )}
                                            </Stack>
                                          </Fieldset>
                                        </Form>
                                        {applicationError ? (
                                          <Center>
                                            <Text c='error.4' mb='md'>
                                              {applicationError}
                                            </Text>
                                          </Center>
                                        ) : null}
                                      </Stack>
                                    )
                                  })()
                                : null}
                            </>
                          ) : null}

                          {selectedApplicationId
                            ? (() => {
                                const selectedApplication = event.application

                                if (!selectedApplication) return null
                                if (!selectedApplication.customFields.length)
                                  return null

                                let textInputCount = 0
                                let textareaCount = 0
                                let radioButtonCount = 0
                                let checkBoxCount = 0

                                return (
                                  <Stack>
                                    <Form id='customForm'>
                                      <Fieldset
                                        legend={
                                          <FormLabel
                                            title='ご記入ください'
                                            require={false}
                                          />
                                        }
                                      >
                                        <Stack>
                                          {selectedApplication.customFields.map(
                                            (field, idx) => {
                                              switch (field.fieldType) {
                                                case 'textInput':
                                                  textInputCount++
                                                  return (
                                                    <CustomTextInput
                                                      key={`custom-text-input-${idx}-${textInputCount}`}
                                                      name={`custom-text-input-${textInputCount}`}
                                                      title={field.title}
                                                      description={
                                                        field.description
                                                      }
                                                      required={field.required}
                                                      error={
                                                        customFormErrors.find(
                                                          (error) =>
                                                            error.name ===
                                                            `custom-text-input-${textInputCount}`,
                                                        )?.errorMessage
                                                      }
                                                    />
                                                  )
                                                case 'textarea': {
                                                  textareaCount++
                                                  return (
                                                    <CustomTextarea
                                                      key={`custom-textarea-${idx}-${textareaCount}`}
                                                      name={`custom-textarea-${textareaCount}`}
                                                      title={field.title}
                                                      description={
                                                        field.description
                                                      }
                                                      required={field.required}
                                                      error={
                                                        customFormErrors.find(
                                                          (error) =>
                                                            error.name ===
                                                            `custom-textarea-${textareaCount}`,
                                                        )?.errorMessage
                                                      }
                                                    />
                                                  )
                                                }
                                                case 'radioButton':
                                                  radioButtonCount++
                                                  return (
                                                    <CustomRadioGroup
                                                      key={`custom-radio-${idx}-${radioButtonCount}`}
                                                      name={`custom-radio-${radioButtonCount}`}
                                                      title={field.title}
                                                      description={
                                                        field.description ?? ''
                                                      }
                                                      options={field.options}
                                                      error={
                                                        customFormErrors.find(
                                                          (error) =>
                                                            error.name ===
                                                            `custom-radio-${radioButtonCount}`,
                                                        )?.errorMessage
                                                      }
                                                    />
                                                  )
                                                case 'checkBox':
                                                  checkBoxCount++
                                                  return (
                                                    <CustomCheckbox
                                                      key={`custom-checkbox-${idx}-${checkBoxCount}`}
                                                      name={`custom-checkbox-${checkBoxCount}`}
                                                      title={field.title}
                                                      description={
                                                        field.description ?? ''
                                                      }
                                                      options={field.options}
                                                      error={
                                                        customFormErrors.find(
                                                          (error) =>
                                                            error.name ===
                                                            `custom-checkbox-${checkBoxCount}`,
                                                        )?.errorMessage
                                                      }
                                                    />
                                                  )
                                              }
                                            },
                                          )}
                                        </Stack>
                                      </Fieldset>
                                    </Form>
                                    {applicationError ? (
                                      <Center>
                                        <Text c='error.4' mb='md'>
                                          {applicationError}
                                        </Text>
                                      </Center>
                                    ) : null}
                                  </Stack>
                                )
                              })()
                            : null}
                        </Stack>

                        {!isKnownUser ? (
                          <Box my='xl'>
                            <Divider my='lg' />
                            <Box my='xl'>
                              <Title my='lg' order={3}>
                                新規会員登録
                              </Title>

                              <Form
                                method='POST'
                                action={`/events/register/${event.id}?form=entryWithSignup`}
                                id='signupForm'
                                onSubmit={handleEntryWithSignupFormSubmit}
                              >
                                <SignupForm
                                  validationErrors={
                                    actionData?.signupFormValidationErrors
                                  }
                                  applicationError={
                                    actionData?.signupFormApplicationError
                                  }
                                />
                                {applicationError ? (
                                  <Center>
                                    <Text c='error.4' mt='md'>
                                      {applicationError}
                                    </Text>
                                  </Center>
                                ) : null}
                                <Box mt='xl'>
                                  <TermPrivacyCheck />
                                </Box>
                                <Container size='sm' mt='xl'>
                                  <Grid justify='center' align='center'>
                                    <Grid.Col
                                      span={{ base: 12, md: 6 }}
                                      order={{ base: 1, md: 2 }}
                                    >
                                      <Button
                                        type='submit'
                                        size='xl'
                                        w='100%'
                                        mah={80}
                                        fw='bold'
                                        disabled={
                                          dayjs().isAfter(
                                            event.applicationEndAt,
                                          ) ||
                                          dayjs().isBefore(
                                            event.applicationStartAt,
                                          )
                                        }
                                        loading={
                                          navigation.state === 'submitting' ||
                                          isLoading
                                        }
                                      >
                                        登録して申し込む
                                      </Button>
                                    </Grid.Col>
                                  </Grid>
                                </Container>
                              </Form>
                            </Box>
                          </Box>
                        ) : null}

                        {/* ユーザーを特定できている */}
                        {isKnownUser ? (
                          <Center>
                            <Grid
                              mt='xl'
                              pt='xl'
                              w={{
                                base: '100%',
                                xs: '100%',
                                sm: '100%',
                                md: '80%',
                                lg: '80%',
                                xl: '80%',
                              }}
                              mih={80}
                            >
                              {isLineConnected ? null : (
                                <Grid.Col span={{ base: 12, md: 6 }}>
                                  <LineButtonWithTooltip
                                    label='LINEを連携'
                                    link={`/auth/line?redirectTo=${encodeURIComponent(
                                      currentLocation.pathname +
                                        currentLocation.search,
                                    )}`}
                                    tooltipLabel={
                                      <Text fw='bold' size='sm'>
                                        LINEを連携すると次回からのログインが便利に！
                                        <br />
                                        最新情報もLINEでお届け！
                                      </Text>
                                    }
                                  />
                                </Grid.Col>
                              )}

                              <Grid.Col
                                span={{
                                  base: 12,
                                  md: isLineConnected ? 12 : 6,
                                  lg: isLineConnected ? 12 : 6,
                                  xl: isLineConnected ? 12 : 6,
                                }}
                              >
                                <Form
                                  method='POST'
                                  action={`/events/register/${event.id}?form=entry`}
                                  id='entry'
                                  onSubmit={handleEntryFormSubmit}
                                >
                                  <Button
                                    type='submit'
                                    size='xl'
                                    w='100%'
                                    disabled={
                                      dayjs().isAfter(event.applicationEndAt) ||
                                      dayjs().isBefore(
                                        event.applicationStartAt,
                                      ) ||
                                      isAlreadyApplied
                                    }
                                    loading={
                                      navigation.state === 'submitting' ||
                                      isLoading
                                    }
                                  >
                                    {isAlreadyApplied
                                      ? '申し込み済み'
                                      : '申し込む'}
                                  </Button>
                                  {actionData?.entryError ? (
                                    <Center my='md'>
                                      <Text c='error'>
                                        {actionData.entryError}
                                      </Text>
                                    </Center>
                                  ) : null}
                                </Form>
                              </Grid.Col>
                            </Grid>
                          </Center>
                        ) : null}
                      </Container>
                    )
                  }}
                </Await>
              </Suspense>
            )
          }}
        </Await>
      </Suspense>
    </>
  )
}
